import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';

import { ENV } from '@/lib/config';
import { apiUrl } from '@/lib/api/api-url';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { i18n } from '@/shared/i18n';
import { withAgentUnavailableDetection } from '@/shared/lib/ai-stream';
import { useMountEffect } from '@/shared/hooks/useMountEffect';

import { parseGeneratedTemplate } from '../../lib/parse-generated-template';
import type { RelayStreamMessage } from '../../model/template-generation.types';
import type { TemplateGenerationStreamProps } from '../../model/useTemplateGeneration/useTemplateGeneration';

const toError = (cause: unknown): Error =>
  cause instanceof Error ? cause : new Error(typeof cause === 'string' ? cause : String(cause));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The stream reports one `data-agent` node per agent invocation, reconciled by `nodeId` and
 * re-emitted as its status changes. The pipeline shows the sub-agents, so the workflow's own root
 * node is skipped: it runs for the whole generation and would light every step at once.
 */
const getAgentNode = (data: unknown): { agentId: string; status: string } | null => {
  if (!isRecord(data) || !isRecord(data.data)) return null;
  const { agentId, status, parentNodeId } = data.data;
  if (typeof agentId !== 'string' || typeof status !== 'string') return null;
  if (parentNodeId === null) return null;
  return { agentId, status };
};

/**
 * The workflow returns its template on its own node, not as assistant text. The text on that
 * message is the sub-agents' raw structured output, which is a different shape and would be
 * rejected, so a succeeded run would report as a failure.
 *
 * The node is reconciled in place as its status changes, so once the run ends this finds the
 * completed one.
 */
const findWorkflowOutput = (parts: RelayStreamMessage['parts'], workflowId: string): unknown => {
  for (const part of parts) {
    if (part.type !== 'data-agent') continue;
    const { agentId, parentNodeId, status, output } = part.data;
    if (parentNodeId === null && agentId === workflowId && status === 'completed') return output;
  }
  return undefined;
};

export const TemplateGenerationStream = ({
  agentId,
  workspaceId,
  payload,
  uploadedAssets,
  onStepEvent,
  onTemplateReady,
  onStreamError,
}: TemplateGenerationStreamProps) => {
  const { sendMessage, stop } = useChat<RelayStreamMessage>({
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      api: apiUrl(ENV.WORKSPACE_API_BASE_URL, `/api/v1/agents/${agentId}/stream`),
      credentials: 'include',
      fetch: withAgentUnavailableDetection(fetchWithAuth),
      prepareSendMessagesRequest: (opts) => {
        let existingHeaders: Record<string, string> = {};
        if (opts.headers instanceof Headers) {
          existingHeaders = Object.fromEntries(opts.headers.entries());
        } else if (opts.headers && typeof opts.headers === 'object') {
          existingHeaders = opts.headers as Record<string, string>;
        }
        return {
          api: opts.api,
          credentials: opts.credentials,
          body: opts.body ?? {},
          headers: {
            ...existingHeaders,
            'x-selected-workspace-id': workspaceId,
          },
        };
      },
    }),
    onData: (data) => {
      if (!isRecord(data) || data.type !== 'data-agent') return;
      const node = getAgentNode(data);
      if (!node) return;

      switch (node.status) {
        case 'running':
          onStepEvent('start', node.agentId);
          break;
        case 'completed':
          onStepEvent('complete', node.agentId);
          break;
        case 'error':
        case 'aborted':
          onStepEvent('error', node.agentId);
          break;
        default:
          break;
      }
    },
    // Called from a `finally`, so an aborted or failed run lands here too. Those already reported
    // through `onError`, and reporting a parse failure on top would replace the real cause.
    onFinish: ({ message, isAbort, isError, isDisconnect }) => {
      if (isAbort || isError || isDisconnect) return;

      const template = parseGeneratedTemplate(findWorkflowOutput(message.parts, agentId));
      if (!template) {
        onStreamError(
          new Error(
            i18n.t('briefs:briefTemplateCreation.templateGenerationStream.malformedTemplateOutput'),
          ),
        );
        return;
      }
      onTemplateReady({ template });
    },
    onError: (cause) => {
      const error = toError(cause);
      if (error.name === 'AbortError') return;
      onStreamError(error);
    },
  });

  // Deferred send pattern from BriefGenerationStream — setTimeout(fn, 0)
  // pushes the initial sendMessage to the next tick so the cleanup function
  // can clear it if the component unmounts in the same render cycle (React
  // StrictMode double-mount).
  useMountEffect(() => {
    let started = false;
    const startTimer = window.setTimeout(() => {
      started = true;
      const input: { name: string; context?: string } = {
        name: payload.name,
      };
      if (payload.instructions.trim().length > 0) {
        input.context = payload.instructions;
      }

      void sendMessage(
        {
          text: '',
          metadata: { isHidden: true },
        } as Parameters<typeof sendMessage>[0],
        {
          body: {
            input: { ...input, artifactIds: payload.artifactIds },
            // References, not bytes: workspace-api resolves each id against assets-api.
            attachments: uploadedAssets.map((asset) => ({
              mediaType: asset.mimeType,
              // eslint-disable-next-line i18next/no-literal-string -- names the host resolver, not copy
              kind: 'asset',
              id: asset.assetId,
              filename: asset.metadata.fileName,
            })),
          },
        },
      );
    }, 0);

    return () => {
      window.clearTimeout(startTimer);
      if (started) void stop();
    };
  });

  return null;
};
